// Request/response types for the staff console: every /v1/internal/*
// route core-platform registers (`grep -n 'HandleFunc("' cmd/core-platform/*.go
// | grep internal`). Each type cites the Go handler that writes it and the
// SQL function whose jsonb it passes through. "0063:N" is
// internal/db/migrations/0063_statement_disputes.up.sql line N, and the
// same for the other migrations. Where the handler and a doc disagree the
// handler wins; web/API-GAPS.md "Staff console" lists the disagreements.

import type { DecimalString, DisputeEvidence, DisputeKind, DisputeResolution, Timestamp, Uuid } from "./types";

/** The `platform_reviewer` role every internal route requires (auth.RequireRole, e.g. review.go:24). */
export const STAFF_ROLE = "platform_reviewer";

/** The {applied, replayed?, result} outcome envelope most SQL commands return. */
export interface StaffOutcome {
  applied: boolean;
  replayed?: boolean;
  result: string;
}

// ---------------------------------------------------------------------------
// Review queue and version review (review.go, review_queue.go; 0019, 0035, 0061)

export type ReviewQueueKind = "config_version" | "creative_version" | "appeal" | "site_tools" | "slot_version";

/** review_queue.go:94-100. */
export interface ReviewQueueConfigItem {
  kind: "config_version";
  version_id: Uuid;
  campaign_id: Uuid;
  advertiser_id: Uuid;
  version_no: number;
  status: "draft" | "in_review";
  created_at: Timestamp;
  /** Commercial terms accepted for this version (review_queue.go:68-69). */
  submitted: boolean;
  pricing_model: string;
  bid_amount: DecimalString;
  currency: string;
  targeting: { categories?: string[] } & Record<string, unknown>;
  category_exclusions: string[] | null;
  publisher_exclusions: Uuid[] | null;
  /** The regulated categories this version targets; approving needs each one confirmed (0061:686-692). */
  regulated_categories: string[];
  requires_regulated_confirmation: boolean;
}

/**
 * review_queue.go's creative_version queue item. `automated_flags` name
 * the policy category only (creative_validate.go:103-105, 212-224).
 *
 * MIGRATION 0078 (owner decision 2026-09-27): domain control is now
 * optional (`domain_verified` shown as informational only, never
 * required for approval unless advertiser_destination_policy.
 * require_domain_control is on — GET /v1/environment's
 * `advertiser_domain_control_required`). `identity_key`, `suspended`,
 * `resolved_final_url`, `redirect_chain` and `final_host_mismatch` are
 * new: the shared-platform page identity (e.g. facebook.com/somepage,
 * not just facebook.com), whether the destination scope is currently
 * suspended, and the most recent scheduled scan's redirect-chain
 * evidence (cmd/background-worker/destination_scan.go). All four
 * scan-derived fields are null until the destination has been scanned
 * at least once (the scheduled scanner only reaches a destination once
 * some campaign's active_release_version points at it).
 */
export interface ReviewQueueCreativeItem {
  kind: "creative_version";
  version_id: Uuid;
  campaign_id: Uuid;
  advertiser_id: Uuid;
  version_no: number;
  status: "draft" | "in_review";
  created_at: Timestamp;
  submitted: boolean;
  title: string;
  body: string;
  format: string;
  destination_url: string;
  destination: {
    host: string;
    /** The shared-platform-aware destination identity (migration 0078). Equal to `host` for a non-shared-platform host. */
    identity_key: string;
    /** Optional informational state — never a requirement unless the policy is on. */
    domain_verified: boolean;
    /** Whether the destination scope currently has an unlifted suspension. */
    suspended: boolean;
    /** The URL actually reached after following redirects, from the most recent scheduled scan. Null if never scanned. */
    resolved_final_url: string | null;
    /** Every URL fetched, in order, from the same scan. Null if never scanned. */
    redirect_chain: string[] | null;
    /** True when resolved_final_url's host is neither destination_url's host nor a subdomain of it. */
    final_host_mismatch: boolean;
  };
  automated_flags: Array<{ category: string }>;
}

/** review_queue.go:180-188. `rejection` is null when the appeal has no linked review event. */
export interface ReviewQueueAppealItem {
  kind: "appeal";
  appeal_id: Uuid;
  campaign_id: Uuid;
  advertiser_id: Uuid;
  version_kind: "config_version" | "creative_version";
  version_id: Uuid;
  basis: string;
  created_at: Timestamp;
  rejection: { reason: string | null; reviewer_user_id: Uuid | null; rejected_at: Timestamp } | null;
}

/** review_inventory.go siteToolsQueueItems (migration 0066, G-33 console). */
export interface ReviewQueueSiteToolsItem {
  kind: "site_tools";
  declaration_id: Uuid;
  site_id: Uuid;
  publisher_id: Uuid;
  domain: string;
  domain_verified: boolean;
  version_no: number;
  created_at: Timestamp;
  tools: Array<{ name: string; description: string; category: string | null }>;
  locale: string | null;
  /** What serving uses until this is decided; null if nothing is approved yet. */
  currently_approved: unknown | null;
  slot_tool_names: string[];
}

/** review_inventory.go slotVersionQueueItems (migration 0066, G-34 console): a pricing-model change awaiting review. */
export interface ReviewQueueSlotVersionItem {
  kind: "slot_version";
  slot_version_id: Uuid;
  slot_id: Uuid;
  site_id: Uuid;
  publisher_id: Uuid;
  domain: string;
  version_no: number;
  tool_name: string;
  pricing_model: string;
  created_at: Timestamp;
  slot_status: string;
  live_version: { slot_version_id: Uuid; pricing_model: string; tool_name: string } | null;
  change: "pricing_model";
}

export type ReviewQueueItem =
  | ReviewQueueConfigItem
  | ReviewQueueCreativeItem
  | ReviewQueueAppealItem
  | ReviewQueueSiteToolsItem
  | ReviewQueueSlotVersionItem;

/** GET /v1/internal/review-queue?kind=&limit= — review_queue.go:35-198, oldest first. */
export interface ReviewQueue {
  as_of: Timestamp;
  items: ReviewQueueItem[];
}

/** POST /v1/internal/{config,creative}-versions/{id}/review body — review.go:42-50. */
export interface ReviewVersionRequest {
  decision: "approve" | "reject";
  reason: string;
  /** Config versions only (0061). */
  regulated_categories_confirmed?: string[];
}

/**
 * The review answer. ALWAYS 200 (review.go:75-77): a refused approval is
 * {applied: false, result: "invalid_transition", reason, ...} in a 200,
 * not an HTTP error. `reason` is version_withdrawn, category_not_cleared
 * (+categories), regulated_review_required (+regulated_categories)
 * (0061:672-692), domain_control_not_verified or wrong_status (0035:980-990);
 * 0019's wrong-status answer has no reason, only `status` (0019:47, 86).
 */
export interface ReviewVersionResponse {
  applied: boolean;
  result: string;
  reason?: string;
  status?: string;
  config_version_id?: Uuid;
  creative_version_id?: Uuid;
  regulated_categories?: string[];
  regulated_categories_confirmed?: string[];
  categories?: string[];
  withdrawn_reason?: string | null;
}

/** POST /v1/internal/appeals/{id}/decide body — review_queue.go:201-205. */
export interface DecideAppealRequest {
  decision: "grant" | "deny";
  reason: string;
  regulated_categories_confirmed?: string[];
}

/**
 * 200 decide_candidate_appeal (0061:946-951). Refusals are 422 typed
 * (outcomeResponse / writeCampaignError): appeal_not_pending,
 * appeal_same_reviewer, decision_reason_required, and a grant refused by an
 * approval gate (regulated_review_required, domain_control_not_verified,
 * category_not_cleared, version_withdrawn); 404 appeal_not_found.
 */
export interface DecideAppealResponse {
  applied: true;
  result: "granted" | "denied";
  appeal_id: Uuid;
  version_id: Uuid;
  version_kind: "config_version" | "creative_version";
  version_status: string;
  decided_at: Timestamp;
}

// ---------------------------------------------------------------------------
// Publisher inventory review (review_inventory.go; migration 0066, G-33/G-34
// console). Neither route was touched by 0068's idempotency/step-up pass
// (API-GAPS.md "Known remaining gaps"): no idempotency key, no step-up,
// and refusals go through writeCampaignError (the 0052/0061 typed-422
// convention), not writeInternalError.

/** POST /v1/internal/site-tool-declarations/{id}/review body — review_inventory.go:122-128. */
export interface ReviewSiteToolsRequest {
  decision: "approve" | "reject";
  reason: string;
  /** The reviewer's classification, covering exactly the declared tools; omitted = the declared categories. */
  classification?: Array<{ name: string; category: string | null }>;
}
/**
 * review_site_tool_declaration (0066). Refusals: 422
 * declaration_not_pending {status}, decision_reason_required,
 * classification_mismatch {declared_tools}, invalid_classification,
 * unknown_category; 404 declaration_not_found.
 */
export interface ReviewSiteToolsResponse extends StaffOutcome {
  result: "approved" | "rejected";
  site_id: Uuid;
  declaration: unknown;
  approved_declaration_id: Uuid | null;
  previous_approved_declaration_id?: Uuid;
}

/** POST /v1/internal/slot-versions/{id}/review body — review_inventory.go (reviewRequest: decision, reason). */
export interface ReviewSlotVersionRequest {
  decision: "approve" | "reject";
  reason: string;
}
/**
 * review_slot_version (0066). Refusals: 422 slot_version_not_pending
 * {status, withdrawn_reason}, decision_reason_required,
 * slot_tool_name_taken {slot_id}; 404 slot_version_not_found.
 */
export interface ReviewSlotVersionResponse extends StaffOutcome {
  result: "approved" | "rejected";
  slot_id: Uuid;
  version: unknown;
  live_version_id?: Uuid;
}

// ---------------------------------------------------------------------------
// Disputes, reviewer side (disputes.go:84-91, 157-210, 225-234, 323-338; 0063)

/** One reviewer vote: statement_line_dispute_votes (0063:330-340), serialized at 0063:716-720. */
export interface DisputeVote {
  actor_user_id: Uuid;
  decision: "uphold" | "deny";
  /** What the uphold vote agreed to move; "0.0000000000" for a deny or a no-money uphold. */
  amount: DecimalString;
  reason: string;
  decided_at: Timestamp;
}

/** statement_line_dispute_json(p_internal := true): the customer view plus 0063:709-724. */
export interface InternalDispute {
  dispute_id: Uuid;
  kind: DisputeKind;
  state: "open" | "resolved";
  outcome: "upheld" | "denied" | null;
  resolution: DisputeResolution | null;
  statement_id: Uuid;
  line_item_id: Uuid;
  line: {
    line_no: number;
    account_type: string;
    currency: string;
    amount: DecimalString;
    description: string;
    source_journal_entry_id: Uuid;
  };
  reservation_id: Uuid | null;
  reason: string;
  filed_at: Timestamp;
  dispute_eligible_until: Timestamp;
  evidence: DisputeEvidence[];
  decided_at: Timestamp | null;
  resolution_note: string | null;
  /** The uphold amount: set by the first uphold vote (0063:1165-1167), final once resolved. */
  amount: DecimalString | null;
  currency: string;
  reversal_journal_entry_id: Uuid | null;
  refund_journal_entry_id: Uuid | null;
  credit_journal_entry_id: Uuid | null;
  amendment_id: Uuid | null;
  party_id: Uuid;
  filed_by: Uuid;
  reservation_dispute_id: Uuid | null;
  /** reservations.state now; 'reversed' means an uphold moves nothing (0063:1092-1096). */
  reservation_state: string | null;
  /** Set once an uphold moving more than 100 was voted (0063:1165-1167). */
  dual_approval_required: boolean | null;
  votes: DisputeVote[];
  amendments: Uuid[];
}

/** GET /v1/internal/disputes?state=open|resolved|all&cursor= — disputes.go:256-313, 323-338. */
export interface InternalDisputeList {
  disputes: InternalDispute[];
  next_cursor: string | null;
}

/** POST /v1/internal/disputes/{id}/decide body — disputes.go:157-163. */
export interface DecideDisputeRequest {
  decision: "uphold" | "deny";
  reason: string;
  /** Exact decimal string; publisher disputes tied to a reservation only. */
  credit_amount?: string;
}

/**
 * 200 decide_statement_line_dispute (0063:1013-1206): result "denied",
 * "awaiting_second_approval" (with approvals/approvals_required) or
 * "upheld". Refusals are 422 typed: decision_reason_required,
 * dispute_already_resolved, reviewer_already_voted,
 * credit_amount_not_applicable, credit_amount_required,
 * invalid_credit_amount, credit_exceeds_share, credit_amount_mismatch,
 * publisher_share_claimed, reservation_not_disputable,
 * reviewer_window_cap_exceeded; 404 dispute_not_found.
 */
export interface DecideDisputeResponse {
  applied: true;
  replayed: boolean;
  result: "denied" | "awaiting_second_approval" | "upheld";
  approvals?: number;
  approvals_required?: number;
  dispute: InternalDispute;
}

// ---------------------------------------------------------------------------
// Safety findings and suspensions (safety.go; 0026, 0032, 0048)

/** POST /v1/internal/safety-findings body — safety.go:39-44. */
export interface CreateSafetyFindingRequest {
  scope_type: "site" | "campaign";
  scope_id: Uuid;
  reason: string;
  /** RFC 3339; omitted means -infinity (every captured reservation in scope). */
  effective_at?: string;
}

/** create_safety_finding (0048:889, 915-916). effective_at is a raw timestamptz; an omitted boundary comes back as "-infinity". */
export interface CreateSafetyFindingResponse extends StaffOutcome {
  result: "created";
  suspension_id: Uuid;
  effective_at: string;
}

/** resolve_safety_finding (0026:542-567). 422 not_yet_confirmed if the finding was never confirmed (safety.go:323-343). */
export interface ResolveSafetyFindingResponse extends StaffOutcome {
  result: "resolved" | "already_resolved";
  suspension_id: Uuid;
}

/**
 * POST /v1/internal/safety-findings/{id}/confirm body — safety.go:173-177
 * (migration 0068). The second, distinct reviewer whose vote starts the
 * financial fan-out (confirm_safety_finding).
 */
export interface ConfirmSafetyFindingRequest {
  notes: string;
  /** Re-send true to proceed anyway once a result: "impact_changed" 422 was shown. */
  accept_updated_impact?: boolean;
}

export interface SafetyImpact {
  reservations: number;
  campaigns: number;
}

/**
 * confirm_safety_finding (0068_internal_route_hardening.up.sql:336-401).
 * "already_lifted" is a harmless replayed-vote no-op (200); "impact_changed"
 * is a genuine typed 422 (safety.go:210-227) carrying the previous and
 * current impact, refused unless the caller re-sends accept_updated_impact.
 */
export interface ConfirmSafetyFindingResponse extends StaffOutcome {
  result: "already_lifted" | "already_confirmed" | "awaiting_second_approval" | "confirmed";
  suspension_id: Uuid;
  approvals?: number;
  approvals_required?: number;
  impact_preview?: SafetyImpact;
  /** Only on a 422 result: "impact_changed" (safety.go:224-226). */
  previous_impact?: SafetyImpact;
  current_impact?: SafetyImpact;
}

/**
 * One row of GET /v1/internal/safety-findings?status= (G-51,
 * 0068_internal_route_hardening.up.sql:852-887). A confirmed finding's
 * `fan_out` starts null and becomes non-null once confirm_safety_finding
 * inserts the progress row.
 */
export interface SafetyFindingRow {
  suspension_id: Uuid;
  scope_type: "site" | "campaign" | "client_version";
  scope_id: Uuid;
  kind: "safety";
  reason: string;
  /** RFC 3339, or the literal "-infinity" for an unbounded look-back. */
  effective_at: string;
  created_at: Timestamp;
  confirmation: { approvals: number; approvals_required: 2; confirmed: boolean };
  fan_out: { status: "in_progress" | "resolved"; processed: number; remaining: number; last_batch_at: Timestamp | null } | null;
  lifted_at: Timestamp | null;
}
export interface SafetyFindingList {
  findings: SafetyFindingRow[];
}

/** One row of GET /v1/internal/suspensions?active= (G-51, any scope/kind). */
export interface SuspensionRow {
  suspension_id: Uuid;
  scope_type: "site" | "campaign" | "client_version";
  scope_id: Uuid;
  kind: "safety" | "ordinary" | string;
  reason: string;
  effective_at: string;
  created_at: Timestamp;
  lifted_at: Timestamp | null;
}
export interface SuspensionList {
  suspensions: SuspensionRow[];
}

/**
 * lift_suspension_with_approval (migration 0068), wrapping suspension_lift
 * (0032:405-428) — a CONFIRMED safety finding now needs a second, distinct
 * reviewer (`result: "awaiting_second_approval"`, then `impact_of_lift`);
 * every other suspension (ordinary, or an unconfirmed safety hold) lifts
 * on one reviewer exactly as before, and now also carries
 * `dual_approval_required: false`. A client_version suspension is still
 * 409 client_version_suspension (safety.go:274-286), before this ever runs.
 */
export interface LiftSuspensionResponse extends StaffOutcome {
  result: "lifted" | "already_lifted" | "awaiting_second_approval";
  suspension_id: Uuid;
  lift_id?: Uuid;
  kind?: string;
  scope_type?: string;
  scope_id?: Uuid;
  scope_version?: number;
  dual_approval_required?: boolean;
  approvals?: number;
  approvals_required?: number;
  impact_of_lift?: SafetyImpact;
}

// ---------------------------------------------------------------------------
// CPE circuit breaker (breaker.go; 0029:198-300)

/** trip_circuit_breaker (0029:219, 248-250). Still single-reviewer and immediate (migration 0068's §5 tighten/loosen asymmetry). */
export interface TripBreakerResponse extends StaffOutcome {
  result: "opened" | "already_open";
  version: number;
  backoff_level?: number;
  half_open_not_before?: string;
}

/**
 * POST /v1/internal/circuit-breaker/close now runs request_breaker_close
 * (migration 0068): a recorded reason, an impact preview, and dual
 * approval by two distinct reviewers before close_circuit_breaker (0029)
 * itself ever runs. Votes are keyed to the breaker's current version, so a
 * stale vote from a superseded open episode never counts toward a new one.
 */
export interface CloseBreakerResponse extends StaffOutcome {
  result: "already_closed" | "awaiting_second_approval" | "closed";
  version: number;
  approvals?: number;
  approvals_required?: number;
  impact_preview?: { cpe_campaigns_affected: number };
}

/**
 * GET /v1/internal/circuit-breaker (G-52) — circuit_breaker_detail().
 * backoff_level and half_open_not_before are the real columns 0029 added
 * (non-null once the breaker has ever tripped, even after it closes —
 * trip_circuit_breaker deliberately doesn't reset them, so a fast
 * re-trip keeps escalating). probes.generation/in_flight (0073) are only
 * non-null/non-zero while state === "half_open": that's the only time a
 * generation is actually being probed.
 */
export interface CircuitBreakerDetail {
  state: "open" | "half_open" | "closed";
  version: number;
  transitioned_at: Timestamp | null;
  transitioned_by: Uuid | null;
  backoff_level: number | null;
  half_open_not_before: Timestamp | null;
  probes: { generation: number | null; in_flight: number };
  pending_close_approvals: Array<{ actor_user_id: Uuid; decided_at: Timestamp }>;
  impact_of_close: { cpe_campaigns_affected: number };
}

// ---------------------------------------------------------------------------
// Publisher debt cases (debt_adjudication.go; 0036, 0037)

/** publisher_debt_case_state (0036:403-410). */
export type DebtCaseState = "pending_review" | "approved" | "rejected" | "appealed" | "appeal_upheld" | "appeal_denied";

/**
 * GET /v1/internal/publisher-debt-cases/{id} — debt_adjudication.go:55-94.
 * Timestamps are raw timestamptz JSON (jsonb_build_object of the column),
 * not _rfc3339. A missing case is 404 case_not_found.
 */
export interface StaffDebtCase {
  case_id: Uuid;
  publisher_id: Uuid;
  advertiser_id: Uuid;
  reservation_id: Uuid;
  currency: string;
  claimed_amount: DecimalString;
  state: DebtCaseState;
  /** claimed_amount > 100 OR cumulative_escalated (0036 _open_publisher_debt_case). */
  dual_approval_required: boolean;
  /** The publisher's 90-day window would pass 2500: dual approval forced. */
  cumulative_escalated: boolean;
  opened_at: string;
  resolved_at: string | null;
  resolution_funding_source: string | null;
  clawback_amount: DecimalString | null;
  debt_recognized: DecimalString | null;
  allowance_provisioned: DecimalString | null;
  advertiser_credited: DecimalString | null;
  approvals: Array<{ phase: "adjudication" | "appeal"; actor_user_id: Uuid; decision: "approve" | "reject"; decided_at: string }>;
  evidence: Array<{ kind: string; recorded_by: Uuid | null; body: unknown; recorded_at: string }>;
  holds: Array<{
    hold_id: Uuid;
    payout_id: Uuid;
    covered_amount: DecimalString;
    resolved_at: string | null;
    resolution: string | null;
    claim_ids: Uuid[] | null;
  }>;
  /**
   * Migration 0068 (G-56): the VIEWING reviewer's own rolling 30-day
   * adjudication-approval total against 0036's 5,000 cap, and the case's
   * publisher's rolling 90-day total against the 2,500 cap — computed
   * live, not just the open-time cumulative_escalated snapshot.
   */
  exposure: {
    actor_window_total: DecimalString;
    actor_window_cap: DecimalString;
    publisher_window_total: DecimalString;
    publisher_window_cap: DecimalString;
  };
}

/**
 * GET /v1/internal/publisher-debt-cases?state=&cursor= (G-49,
 * list_publisher_debt_cases). pending_review is oldest first (a queue);
 * appealed/all are newest first. next_cursor is re-encoded server-side
 * (reencodeSQLNextCursor, publisher_reads.go) into the same opaque
 * base64 string every other paginated list uses, so it round-trips
 * through ?cursor= like any other list.
 */
export interface StaffDebtCaseList {
  cases: StaffDebtCase[];
  next_cursor: string | null;
}

/** POST .../evidence body — debt_adjudication.go:106-109. `body` is any JSON object. */
export interface DebtCaseEvidenceRequest {
  kind: string;
  body: Record<string, unknown>;
}

/** POST .../adjudicate and .../appeal/decide body — debt_adjudication.go:145-148. */
export interface DebtCaseDecisionRequest {
  decision: "approve" | "reject";
  notes: string;
}

/**
 * adjudicate_publisher_debt_case (0036:1139-1223) and
 * decide_publisher_debt_appeal (0037:715-857). Every refusal is RAISE
 * 'rejected: ...', which writeRejected turns into an untyped 400
 * request_rejected (publisher.go:131-134): the reason (same actor twice,
 * actor window cap, an original adjudicator deciding the appeal) never
 * reaches the client.
 */
export interface DebtCaseDecisionResponse {
  applied: boolean;
  replayed: boolean;
  result:
    | "rejected"
    | "awaiting_second_approval"
    | "approved"
    | "already_resolved"
    | "appeal_denied"
    | "appeal_upheld"
    | "no_open_appeal"
    | "appealed"
    | "already_appealed"
    | "evidence_recorded";
  case_id?: Uuid;
  approvals?: number;
  approvals_required?: number;
  state?: DebtCaseState;
  resolution?: {
    case_id: Uuid;
    outcome: "upheld" | "rejected";
    clawback_amount: DecimalString;
    debt_recognized: DecimalString;
    allowance_provisioned: DecimalString;
  };
  derecognized_debt?: DecimalString;
  previously_written_off?: DecimalString;
  restored_entitlement?: DecimalString;
}

// ---------------------------------------------------------------------------
// Receivable write-off (receivable_write_off.go; 0037)

/** The URL spelling (receivable_write_off.go:60-69). */
export type ReceivableKindPath = "advertiser-receivable" | "publisher-debt";

/** One past write-off, receivable_write_off.go:101-112. */
export interface WriteOffRecord {
  write_off_id: Uuid;
  amount: DecimalString;
  allowance_consumed: DecimalString;
  expense_recognized: DecimalString;
  outstanding_before: DecimalString;
  outstanding_after: DecimalString;
  reason: string;
  actor_user_id: Uuid;
  journal_entry_id: Uuid;
  written_off_at: string;
}

/**
 * One receivable_collections row, internal view (p_internal := true) —
 * _receivable_case_json, 0067_receivable_collection.up.sql:1245-1258.
 */
export interface InternalReceivableCollectionEvent {
  collection_id: Uuid;
  method: string;
  amount: DecimalString;
  outstanding_after: DecimalString;
  collected_at: Timestamp;
  deposit_id: Uuid | null;
  payout_id: Uuid | null;
  reinstates_collection_id: Uuid | null;
  allowance_amount: DecimalString;
  journal_entry_id: Uuid;
  allowance_journal_entry_id: Uuid | null;
  postings: Array<{ posting_id: Uuid; amount: DecimalString }>;
}

/**
 * receivable_case_view(caseId, NULL, true) — migration 0067's collection
 * history (Stripe payments, deposit netting, payout offsets and their
 * reinstatement), added to the write-off screen's read
 * (receivable_write_off.go:183-194).
 */
export interface InternalReceivableCollection {
  case_id: Uuid;
  case_source: string;
  receivable_kind: "advertiser_receivable" | "publisher_debt";
  currency: string;
  state: string;
  recognized: DecimalString;
  written_off: DecimalString;
  collected: DecimalString;
  outstanding: DecimalString;
  collectible: boolean;
  opened_at: Timestamp;
  settle_by: "payment_or_next_deposit" | "offset_against_earnings" | null;
  payment_path: string | null;
  collections: InternalReceivableCollectionEvent[];
  party_id: Uuid;
  allowance_provisioned: DecimalString;
  allowance_released: DecimalString;
  allowance_consumed_by_write_offs: DecimalString;
  allowance_remaining: DecimalString;
}

/** GET /v1/internal/receivables/{kind}/{caseId} — receivable_outstanding (0037:413-426) + history (receivable_write_off.go:133). */
export interface ReceivableOutstanding {
  receivable_kind: "advertiser_receivable" | "publisher_debt";
  case_id: Uuid;
  party_id: Uuid;
  currency: string;
  case_state: string;
  recognized: DecimalString;
  written_off_total: DecimalString;
  outstanding: DecimalString;
  allowance_provisioned: DecimalString;
  allowance_remaining: DecimalString;
  state_permits_write_off: boolean;
  write_offs: WriteOffRecord[];
  /** Migration 0067 (receivable_write_off.go:183-194): the case's collection history. */
  collection: InternalReceivableCollection;
}

/**
 * One row of GET /v1/internal/receivables?kind=&writable=&cursor= (G-50,
 * list_internal_receivables). Enumerates every 0067 receivable origin
 * (a lost chargeback, a standing debt case, a double-payment correction)
 * with outstanding > 0.
 */
export interface InternalReceivableRow {
  receivable_kind: "advertiser_receivable" | "publisher_debt";
  case_id: Uuid;
  case_source: "chargeback" | "debt_case" | "provider_correction";
  party_id: Uuid;
  currency: string;
  case_state: string;
  recognized: DecimalString;
  written_off_total: DecimalString;
  collected_total: DecimalString;
  outstanding: DecimalString;
  allowance_provisioned: DecimalString;
  allowance_released: DecimalString;
  allowance_remaining: DecimalString;
  collectible: boolean;
  /** False for a double-payment (provider_correction) receivable: G-50 still lists it, but write_off_receivable can't act on it. */
  state_permits_write_off: boolean;
  opened_at: Timestamp;
}
/**
 * next_cursor is re-encoded server-side (reencodeSQLNextCursor,
 * publisher_reads.go) into the same opaque base64 string
 * publisherPageParams/decodeCampaignCursor (campaign_read.go:23-41)
 * expects back on `?cursor=`, so WriteOffs.tsx round-trips it like any
 * other paginated list.
 */
export interface InternalReceivableList {
  receivables: InternalReceivableRow[];
  next_cursor: string | null;
}

/** POST .../write-off body — receivable_write_off.go:144-148. Empty amount = everything outstanding. */
export interface WriteOffRequest {
  amount?: string;
  reason: string;
}

/** write_off_receivable (0037:527-533 replay, 685-699). */
export interface WriteOffResponse {
  applied: true;
  replayed: boolean;
  result: "written_off" | "already_written_off";
  write_off_id: Uuid;
  receivable_kind: string;
  case_id: Uuid;
  party_id?: Uuid;
  currency?: string;
  amount: DecimalString;
  allowance_consumed?: DecimalString;
  expense_recognized?: DecimalString;
  outstanding_before?: DecimalString;
  outstanding_after: DecimalString;
  fully_written_off?: boolean;
  journal_entry_id?: Uuid;
}

// ---------------------------------------------------------------------------
// Provider operations (provider_operations.go; 0043, 0050)

/** provider_operation_status (0008:79-81). */
export type ProviderOperationStatus = "prepared" | "submitting" | "unknown" | "confirmed" | "failed";
export type ProviderDecision = "confirmed" | "failed" | "acknowledged" | "double_payment";

export interface ProviderOperationDecisionRecord {
  actor_user_id: Uuid;
  decision: ProviderDecision;
  provider_object_id: string | null;
  reason: string;
  evidence: unknown;
  resulted_in: string;
  /** Votes count only while this equals the operation's current attachment (0050:1917-1928). */
  attached_object_id_at_decision: string | null;
  decided_at: string;
}

/** One row of GET /v1/internal/provider-operations/unresolved — provider_operations.go:84-149. */
export interface ProviderOperation {
  provider_operation_id: Uuid;
  operation_type: "payout" | "refund" | "transfer_reversal" | string;
  our_reference_id: Uuid;
  status: ProviderOperationStatus;
  provider_idempotency_key: string;
  provider_object_id: string | null;
  created_at: string;
  submission_attempted_at: string | null;
  recovery_deadline: string | null;
  reference_metadata_sent: boolean | null;
  resolution_checked_at: string | null;
  manual_review_reason: string | null;
  manual_review_at: string | null;
  payout: {
    payout_id: Uuid;
    publisher_id: Uuid;
    state: string;
    amount: DecimalString;
    currency: string | null;
    stripe_connect_account_id: string | null;
  } | null;
  transfer_reversal: { transfer_reversal_id: Uuid; payout_id: Uuid; case_id: Uuid; amount: DecimalString; state: string } | null;
  refund: {
    refund_id: Uuid;
    advertiser_id: Uuid;
    deposit_id: Uuid;
    state: string;
    amount: DecimalString;
    currency: string;
    encumbered_amount: DecimalString;
    stripe_payment_intent_id: string | null;
  } | null;
  decisions: ProviderOperationDecisionRecord[];
  /** Leads only: Stripe objects matching the destination, attached to no operation. Nothing finalizes from one. */
  suggestions: Array<{
    provider_object_id: string;
    last_event_id: string;
    last_event_type: string;
    /** Stripe's integer minor units, as Stripe sent it. */
    amount: number | null;
    currency: string | null;
    status: string | null;
    amount_reversed: number | null;
    created: number | null;
    metadata: Record<string, unknown> | null;
  }>;
}

/** GET /v1/internal/provider-operations/unresolved — provider_operations.go:151-163. No paging. */
export interface ProviderOperationList {
  operations: ProviderOperation[];
}

/** POST .../decision body — provider_operations.go:165-170. */
export interface ProviderDecisionRequest {
  decision: ProviderDecision;
  provider_object_id: string;
  reason: string;
  evidence: Record<string, unknown>;
}

/**
 * decide_provider_operation (0050:1840-2006). Refusals are 409
 * {"error": "request_rejected", "detail": "<text>"} with the SQL message
 * as a STRING (provider_operations.go:209-214), not a typed code.
 */
export interface ProviderDecisionResponse {
  applied: true;
  replayed: boolean;
  result: "acknowledged" | "confirmed" | "awaiting_second_approval" | "failed" | "double_payment_corrected";
  provider_operation_id: Uuid;
  approvals?: number;
  approvals_required?: number;
  provider_object_id?: string;
  finalize?: unknown;
  correction?: unknown;
}

// ---------------------------------------------------------------------------
// Approved client matrix (client_matrix.go; 0047, 0048)

export type ClientMatrixStatus = "validated" | "safety_suspended" | "ordinary_suspended";
export type EvidenceClass = "independently_verified" | "publisher_attested";

/** One entry of GET /v1/internal/client-matrix — client_matrix.go:69-100. */
export interface ClientMatrixEntry {
  approved_client_matrix_id: Uuid;
  client_name: string;
  version: string;
  status: ClientMatrixStatus;
  evidence_class: EvidenceClass;
  created_at: string;
  scope_version: number | null;
  active_suspensions: Array<{ suspension_id: Uuid; kind: string; reason: string; effective_at: string }>;
  events: Array<{
    event_id: Uuid;
    action: "created" | "revalidated" | "status_changed";
    source: "staff_command" | "suspension_machinery" | "direct_write" | "migration";
    actor_user_id: Uuid | null;
    status_before: ClientMatrixStatus | null;
    status_after: ClientMatrixStatus | null;
    evidence_class_before: EvidenceClass | null;
    evidence_class_after: EvidenceClass | null;
    reason: string | null;
    evidence: unknown;
    created_suspension_ids: Uuid[] | null;
    lifted_suspension_ids: Uuid[] | null;
    occurred_at: string;
  }>;
}

export interface ClientMatrixList {
  entries: ClientMatrixEntry[];
}

/** POST /v1/internal/client-matrix body — client_matrix.go:128-134. evidence: a non-empty JSON object. */
export interface CreateClientMatrixRequest {
  client_name: string;
  version: string;
  evidence_class: EvidenceClass;
  reason: string;
  evidence: Record<string, unknown>;
}

/** POST .../{id}/validate body — client_matrix.go:171-175. Empty evidence_class keeps the class. */
export interface RevalidateClientMatrixRequest {
  evidence_class?: EvidenceClass;
  reason: string;
  evidence: Record<string, unknown>;
}

/**
 * POST .../{id}/status body — client_matrix.go:251-259. Suspending stays
 * single-reviewer and immediate; migration 0069 refuses status:"validated"
 * here outright (409 reinstatement_requires_dual_control) — reinstating
 * is ReinstateClientMatrixRequest below.
 */
export interface SetClientMatrixStatusRequest {
  status: "ordinary_suspended" | "safety_suspended";
  reason: string;
  evidence?: Record<string, unknown>;
  effective_at?: string;
}

/**
 * create_approved_client (0047:522-524, 201), revalidate_approved_client
 * (0047:575-577), set_approved_client_status (0048:1099-1125; "unchanged"
 * is applied:false). Refusals are 409 request_rejected with a string
 * detail (client_matrix.go:277-287).
 */
export interface ClientMatrixCommandResponse {
  applied: boolean;
  replayed: boolean;
  result: "created" | "revalidated" | "status_changed" | "unchanged";
  approved_client_matrix_id: Uuid;
  status: ClientMatrixStatus;
  status_before?: ClientMatrixStatus;
  evidence_class?: EvidenceClass;
  evidence_class_before?: EvidenceClass;
  created_suspension_ids?: Uuid[];
  lifted_suspension_ids?: Uuid[];
  scope_version?: number | null;
  held?: string[];
}

/**
 * POST .../{id}/reinstate body — client_matrix.go:322-326. Evidence is
 * required on every vote (not just the one that ends up applying it):
 * reinstating re-asserts §3's "Phase-0-validated combination".
 */
export interface ReinstateClientMatrixRequest {
  reason: string;
  evidence: Record<string, unknown>;
}

/**
 * POST .../{id}/reinstate response — client_matrix.go:328-368,
 * request_client_matrix_reinstatement (0069:540-599). Migration 0069's
 * dual control: any platform_reviewer may cast the first vote
 * (result:"awaiting_second_approval", nothing applied yet); a SECOND,
 * DISTINCT reviewer's call is what actually reinstates
 * (result:"status_changed", via the unchanged set_approved_client_status),
 * merged with `dual_approval_required: true` and the final `approvals`
 * count. Already-validated is a harmless no-op (result:"already_validated",
 * applied:false), the same "replay against current state" shape every
 * other client-matrix command uses. Votes are scoped to the
 * client_version's current scope_version, so a stale vote from an earlier
 * suspend/reinstate cycle never counts toward a new one.
 */
export interface ReinstateClientMatrixResponse {
  applied: boolean;
  replayed?: boolean;
  result: "awaiting_second_approval" | "status_changed" | "already_validated";
  approved_client_matrix_id: Uuid;
  status: ClientMatrixStatus;
  status_before?: ClientMatrixStatus;
  approvals?: number;
  approvals_required?: number;
  dual_approval_required?: boolean;
  evidence_class?: EvidenceClass;
  evidence_class_before?: EvidenceClass;
  created_suspension_ids?: Uuid[];
  lifted_suspension_ids?: Uuid[];
  scope_version?: number | null;
  held?: string[];
}

// ---------------------------------------------------------------------------
// Reconciliation quarantines (reconciliation.go; migration 0072)

/** financial_quarantines.scope (0072): a party's own halts, or the ledger-wide payout halt. */
export type QuarantineScope = "party" | "platform_payouts";

/** The checks _reconciliation_findings runs (0072's header, section 1). */
export type ReconciliationCheck =
  | "account_balance"
  | "account_posting_sequence"
  | "journal_entry_balance"
  | "budget_period_reserved"
  | "budget_period_captured"
  | "deposit_refunded_total"
  | "deposit_disputed_total"
  | "posting_claimed_total";

export type QuarantineResolution = "repaired" | "explained";

/** One clear vote (financial_quarantine_clear_votes). `current` is false for a vote cast against an older version. */
export interface QuarantineVote {
  actor_user_id: Uuid;
  resolution: QuarantineResolution;
  explanation: string;
  quarantine_version: number;
  current: boolean;
  decided_at: Timestamp;
}

/**
 * _financial_quarantine_json (0072). Every amount is an exact decimal
 * string, SUM(|materialized - expected - baseline|) over the quarantine's
 * money discrepancies; counts (posting sequence) are not money and are
 * not in them.
 */
export interface FinancialQuarantine {
  quarantine_id: Uuid;
  scope: QuarantineScope;
  party_id: Uuid;
  party_type: "advertiser" | "publisher" | "platform";
  party_name: string | null;
  status: "open" | "cleared";
  /** The check that opened it. */
  reason: ReconciliationCheck | string;
  /** What it halts: "new_reservations" (advertiser), "payouts" (publisher, or every publisher for platform_payouts). */
  blocks: string[];
  opened_at: Timestamp;
  age_seconds: number;
  /** 0 until the run that opened it notifies; 1 opened, 2 past warn_after, 3 past critical_after. */
  escalation_level: number;
  escalated_at: Timestamp | null;
  first_seen_discrepancy: DecimalString;
  current_discrepancy: DecimalString;
  peak_discrepancy: DecimalString;
  cumulative_discrepancy: DecimalString;
  /** Bumped whenever a run changes the picture; clear votes bind to it. */
  version: number;
  last_changed_at: Timestamp;
  last_checked_at: Timestamp | null;
  /** Discrepancies still present at the last run. */
  open_discrepancies: number;
  /** Discrepancies the last run found consistent again. */
  consistent_discrepancies: number;
  votes: QuarantineVote[];
  approvals_required: number;
  cleared_at: Timestamp | null;
  resolution: QuarantineResolution | null;
  resolution_explanation: string | null;
}

/** _reconciliation_discrepancy_json (0072). Raw figures; `difference` is materialized - expected before `baseline`. */
export interface ReconciliationDiscrepancy {
  discrepancy_id: Uuid;
  quarantine_id: Uuid;
  check: ReconciliationCheck | string;
  subject_type: string;
  subject_key: string;
  account_id: Uuid | null;
  currency: string | null;
  unit: "money" | "count";
  first_materialized: DecimalString;
  first_expected: DecimalString;
  first_difference: DecimalString;
  materialized: DecimalString;
  expected: DecimalString;
  difference: DecimalString;
  baseline: DecimalString;
  first_seen_at: Timestamp;
  last_seen_at: Timestamp;
  observations: number;
  state: "present" | "consistent" | "closed";
  absent_since: Timestamp | null;
  closed_at: Timestamp | null;
  /** Only on the cross-quarantine listing. */
  party_id?: Uuid;
  scope?: QuarantineScope;
}

/** GET /v1/internal/reconciliation/quarantines/{id}: the summary plus every discrepancy. */
export interface FinancialQuarantineDetail extends FinancialQuarantine {
  discrepancies: ReconciliationDiscrepancy[];
}

/** GET /v1/internal/reconciliation/quarantines (list_financial_quarantines, newest first, at most 500). */
export interface FinancialQuarantineList {
  quarantines: FinancialQuarantine[];
}

/** GET /v1/internal/reconciliation/discrepancies (list_reconciliation_discrepancies). */
export interface ReconciliationDiscrepancyList {
  discrepancies: ReconciliationDiscrepancy[];
}

/**
 * GET /v1/internal/reconciliation — reconciliation_overview() plus
 * `override`, which reconciliation.go adds: the degraded-mode override is
 * NOT built (0072's header, section 6), so its state is always
 * "not_implemented"; `cap` is VADS_RECONCILIATION_OVERRIDE_CAP.
 */
export interface ReconciliationOverview {
  last_run: {
    run_id: number;
    lease_owner: string;
    started_at: Timestamp;
    finished_at: Timestamp;
    tolerance: DecimalString;
    accounts_checked: number;
    budget_periods_checked: number;
    deposits_checked: number;
    claim_totals_checked: number;
    findings: number;
    ledger_balanced: boolean;
    quarantines_opened: number;
    escalations: number;
    open_quarantines: number;
    aggregate_discrepancy: DecimalString;
  } | null;
  open_quarantines: number;
  /** SUM(current_discrepancy) over open party quarantines. */
  aggregate_discrepancy: DecimalString;
  platform_payouts_halted: boolean;
  risk_funding: Array<{
    currency: string;
    platform_risk_spend_asset: DecimalString;
    degraded_serving_subsidy_expense: DecimalString;
  }>;
  override: { state: "not_implemented" | string; cap: DecimalString; detail: string };
}

/** POST .../quarantines/{id}/clear body (reconciliation.go clearQuarantineRequest). */
export interface ClearQuarantineRequest {
  resolution: QuarantineResolution;
  explanation: string;
  /** The version the reviewer was shown; a stale one is a 422 quarantine_changed. */
  expected_version?: number;
}

/**
 * clear_financial_quarantine (0072). awaiting_second_approval until a
 * second, distinct reviewer votes the same resolution at the same
 * version; already_cleared is a harmless no-op.
 */
export interface ClearQuarantineResponse {
  applied: boolean;
  replayed?: boolean;
  result: "awaiting_second_approval" | "cleared" | "already_cleared";
  quarantine_id: Uuid;
  quarantine_version?: number;
  resolution: QuarantineResolution;
  approvals?: number;
  approvals_required?: number;
  stale_votes?: number;
  cleared_at?: Timestamp;
}

// ---------------------------------------------------------------------------
// Promotional credit (promotions.go; migration 0075)

export type PromotionState = "live" | "scheduled" | "lapsed" | "ended";

/** _promotion_json (0075), with list_promotions' totals. */
export interface Promotion {
  promotion_id: Uuid;
  name: string;
  description: string;
  code: string | null;
  currency: string;
  applies_to_deposits: boolean;
  min_deposit_amount: DecimalString;
  credit_kind: "fixed" | "match";
  fixed_amount: DecimalString | null;
  match_rate: string | null;
  match_cap: DecimalString | null;
  per_advertiser_limit: number;
  credit_expiry_days: number;
  valid_from: Timestamp;
  valid_until: Timestamp | null;
  created_by: Uuid;
  created_at: Timestamp;
  ended_at: Timestamp | null;
  ended_by: Uuid | null;
  end_reason: string | null;
  state: PromotionState;
  grants?: number;
  granted_total?: DecimalString;
  outstanding_total?: DecimalString;
  retired_total?: DecimalString;
  used_total?: DecimalString;
}

export interface PromotionList {
  promotions: Promotion[];
}

/** POST /v1/internal/promotions (create_promotion). Amounts are decimal strings. */
export interface CreatePromotionRequest {
  name: string;
  description?: string;
  code?: string;
  applies_to_deposits?: boolean;
  min_deposit_amount?: string;
  credit_kind: "fixed" | "match";
  fixed_amount?: string;
  match_rate?: string;
  match_cap?: string;
  per_advertiser_limit?: number;
  credit_expiry_days?: number;
  valid_from?: string;
  valid_until?: string;
}

export interface PromotionCommandResponse extends StaffOutcome {
  promotion: Promotion;
}

/** POST /v1/internal/promotions/{id}/grants (grant_promo_credit). */
export interface GrantPromoCreditRequest {
  advertiser_id: Uuid;
  /** Omitted: a fixed promotion's amount. */
  amount?: string;
  reason: string;
}

export interface GrantPromoCreditResponse extends StaffOutcome {
  grant_id: Uuid;
  promotion_id: Uuid;
  advertiser_id: Uuid;
  amount: DecimalString;
  currency: string;
  expires_at: Timestamp;
}

/** promo_liability_report, one row per currency. */
export interface PromoLiabilityCurrency {
  currency: string;
  promo_credit_outstanding: DecimalString;
  promo_credit_spendable: DecimalString;
  promo_credit_awaiting_expiry_sweep: DecimalString;
  promo_credit_in_flight: DecimalString;
  advertisers_with_credit: number;
  promotional_earnings_outstanding: DecimalString;
  promotional_earnings_eligible_now: DecimalString;
  publishers_with_promotional_earnings: number;
  pool_balance: DecimalString;
  commitments_balance: DecimalString;
  marketing_expense_to_date: DecimalString;
  converted_to_date: DecimalString;
}

export interface PromoLiabilityReport {
  as_of: Timestamp;
  currencies: PromoLiabilityCurrency[];
}

export interface PromoPoolFunding {
  funding_id: Uuid;
  currency: string;
  amount: DecimalString;
  explanation: string;
  /** expired: a proposal nobody approved within 24 hours. */
  status: "proposed" | "executed" | "expired";
  proposed_by: Uuid;
  proposed_at: Timestamp;
  approved_by: Uuid | null;
  executed_at: Timestamp | null;
  eligible_before: DecimalString | null;
  converted_amount: DecimalString | null;
  pool_balance_after: DecimalString | null;
  publishers_converted: number | null;
}

/** GET /v1/internal/promotions/pool. */
export interface PromoPool {
  preview: { currency: string; eligible_total: DecimalString; eligible_publishers: number; pool_balance: DecimalString };
  fundings: PromoPoolFunding[];
}

export interface ProposePoolFundingRequest {
  amount: string;
  explanation: string;
  currency?: string;
}

export interface PoolFundingResponse extends StaffOutcome {
  funding: PromoPoolFunding;
  approvals?: number;
  approvals_required?: number;
  preview?: PromoPool["preview"];
}

// ---------------------------------------------------------------------------
// Decision-endpoint rate limiting (site_rate_limit.go, migration 0077,
// PLAN.md §9). GET is a plain staff read; PUT (set or clear) needs a
// recent MFA step-up and an idempotency key.

export interface SiteRateLimitOverride {
  rate_per_sec: DecimalString;
  burst: number;
  updated_at: Timestamp;
  updated_by: Uuid;
}

/** GET /v1/internal/sites/{id}/rate-limit. */
export interface SiteRateLimitResponse {
  site_id: Uuid;
  /** null when the site serves at the platform default. */
  override: SiteRateLimitOverride | null;
  default_rate_per_sec: DecimalString;
  default_burst: number;
}

/** PUT /v1/internal/sites/{id}/rate-limit body: set (rate_per_sec + burst) or clear. */
export type SetSiteRateLimitRequest = { clear: true } | { rate_per_sec: string; burst: number };

export interface SetSiteRateLimitResponse extends StaffOutcome {
  result: "set" | "cleared";
  site_id: Uuid;
  rate_per_sec?: DecimalString;
  burst?: number;
}
